import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Database, ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { AddResourceInstanceButton } from "./AddResourceInstanceButton"
import { EditControlPlaneResourceInstanceComposer } from "@/forms/rema/editControlPlaneResourceInstance/composer"

export interface ResourceInstance {
  id: string
  instanceNumber: number
  instanceName: string
  instanceDescription: string
  instancePath: string
  instanceAccessPolicy: string
  resourceTypeNumber: number
  resourceTypeName: string
}

export function ControlPlaneResourceInstanceList() {
  const [searchQuery, setSearchQuery] = React.useState("")
  const [currentPage, setCurrentPage] = React.useState(1)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)
  const [selectedInstance, setSelectedInstance] = React.useState<ResourceInstance | null>(null)
  const itemsPerPage = 10

  // Fetch resource instances from API
  const { data, isLoading, isError, refetch } = useApiQuery(
    "getControlPlaneResourceInstances",
    {}
  )

  // Map API response to component state
  const instances: ResourceInstance[] = (data?.controlPlaneResourceInstances || []).map((inst: any) => ({
    id: inst.id || '',
    instanceNumber: inst.resourceInstanceNumber || 0,
    instanceName: inst.resourceInstanceName || '',
    instanceDescription: inst.resourceInstanceDescription || '',
    instancePath: inst.resourceInstancePath || '',
    instanceAccessPolicy: inst.resourceAccessPolicy || '',
    resourceTypeNumber: inst.resourceTypeNumber || 0,
    resourceTypeName: inst.resourceTypeName || 'Unknown Type',
  }))

  // Filter instances by search query
  const filteredInstances = instances.filter((inst) =>
    inst.instanceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inst.instanceDescription?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Pagination on total instance count
  const totalPages = Math.ceil(filteredInstances.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedInstances = filteredInstances.slice(startIndex, startIndex + itemsPerPage)

  // No grouping needed - show as flat list with type name in each row

  const handleEdit = (instance: ResourceInstance) => {
    setSelectedInstance(instance)
    setIsEditDialogOpen(true)
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load resource instances</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="border-b bg-muted/30">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Manage Resource Instances - Control Plane</CardTitle>
              <CardDescription>Resource instances grouped by type</CardDescription>
            </div>
          </div>

          <AddResourceInstanceButton onSuccess={() => refetch()} />
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Search */}
        <div className="border-b p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search instances..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setCurrentPage(1)
              }}
              className="pl-9"
            />
          </div>
        </div>

        {/* Flat Table with Type Name Column */}
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              <TableHead className="font-semibold">Resource Type</TableHead>
              <TableHead className="w-[100px] font-semibold">ID</TableHead>
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Description</TableHead>
              <TableHead className="font-semibold">Path</TableHead>
              <TableHead className="w-[100px] text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedInstances.length > 0 ? (
              paginatedInstances.map((instance) => (
                <TableRow key={instance.id} className="transition-colors hover:bg-muted/50">
                  <TableCell className="font-medium">
                    {instance.resourceTypeName}
                  </TableCell>
                  <TableCell className="font-medium">
                    {instance.instanceNumber}
                  </TableCell>
                  <TableCell className="font-medium">
                    {instance.instanceName}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {instance.instanceDescription || '-'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {instance.instancePath}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(instance)}
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit {instance.instanceName}</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No resource instances found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, filteredInstances.length)} of{" "}
              {filteredInstances.length} instances
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Resource Instance</DialogTitle>
            <DialogDescription>
              Update the resource instance configuration below.
            </DialogDescription>
          </DialogHeader>
          <EditControlPlaneResourceInstanceComposer
            initialValues={{
              resourceTypeNumber: selectedInstance?.resourceTypeNumber,
              instanceName: selectedInstance?.instanceName ?? '',
              instanceDescription: selectedInstance?.instanceDescription ?? '',
              instancePath: selectedInstance?.instancePath ?? '',
              instanceAccessPolicy: selectedInstance?.instanceAccessPolicy ?? '',
            }}
            showHeader={false}
            submitLabel="Save"
            onSuccess={() => {
              setIsEditDialogOpen(false)
              setSelectedInstance(null)
              refetch()
            }}
            onError={(error) => {
              alert(`Failed to update instance: ${error.message}`)
            }}
            onCancel={() => {
              setIsEditDialogOpen(false)
              setSelectedInstance(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </Card>
  )
}
